import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { 
  Mail, 
  Send, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Settings,
  Server,
  AlertTriangle,
  Info,
  Lock
} from 'lucide-react';

const EmailConfigPage = () => {
  const { user, hasPermission } = useAuth();
  const [emailConfig, setEmailConfig] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  // Verificar permissão: nível de acesso >= 5 E permissão configuracoes.email
  const temAcesso = user?.nivel_acesso >= 5 && hasPermission('configuracoes.email');

  useEffect(() => {
    if (temAcesso) {
      checkEmailConfig();
    } else {
      setLoading(false);
    }
  }, [temAcesso]);

  const checkEmailConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/test-email');
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Erro ao verificar email:', error.response?.data);
      setEmailConfig({
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Erro ao verificar configuração',
        errorCode: error.response?.data?.errorCode,
        errorDetails: error.response?.data?.errorDetails,
        configured: error.response?.data?.configured || false,
        config: error.response?.data?.config
      });
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async (e) => {
    e.preventDefault();
    
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Digite um email para teste.' });
      return;
    }

    setSending(true);
    setMessage(null);

    try {
      const response = await api.post('/auth/send-test-email', { email: testEmail });
      setMessage({
        type: 'success',
        text: response.data.message || 'Email de teste enviado com sucesso!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Erro ao enviar email de teste.'
      });
    } finally {
      setSending(false);
    }
  };

  // Verificação de acesso negado
  if (!user?.nivel_acesso || user.nivel_acesso < 5) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Acesso Negado</h2>
          <p className="mt-2 text-gray-600">Apenas administradores podem acessar esta página.</p>
        </div>
      </div>
    );
  }

  // Verificação de permissão específica
  if (!hasPermission('configuracoes.email')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-amber-500" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Permissão Necessária</h2>
          <p className="mt-2 text-gray-600">Você não tem permissão para configurar emails.</p>
          <p className="mt-1 text-sm text-gray-500">Permissão requerida: <code className="bg-gray-100 px-2 py-1 rounded">configuracoes.email</code></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Mail className="h-7 w-7 mr-3 text-indigo-600" />
            Configuração de Email
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Verifique e teste a configuração do servidor de email para recuperação de senha.
          </p>
        </div>

        {/* Status Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Server className="h-5 w-5 mr-2 text-gray-500" />
              Status do Servidor
            </h2>
          </div>
          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center text-gray-500">
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                Verificando configuração...
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status Principal */}
                <div className="flex items-center">
                  {emailConfig?.success ? (
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-500 mr-3" />
                  )}
                  <span className={`text-lg font-medium ${emailConfig?.success ? 'text-green-700' : 'text-red-700'}`}>
                    {emailConfig?.message}
                  </span>
                </div>

                {/* Detalhes do Erro (se houver) */}
                {!emailConfig?.success && (emailConfig?.errorCode || emailConfig?.errorDetails) && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">
                      <strong>Código do erro:</strong> {emailConfig.errorCode || 'N/A'}
                    </p>
                    {emailConfig.errorDetails && (
                      <p className="text-sm text-red-800 mt-1">
                        <strong>Detalhes:</strong> {emailConfig.errorDetails}
                      </p>
                    )}
                  </div>
                )}

                {/* Detalhes da Configuração */}
                {emailConfig?.config && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Provedor</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {emailConfig.provider || 'SMTP'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Servidor</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {emailConfig.config.host}:{emailConfig.config.port}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Usuário</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {emailConfig.config.user}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Remetente</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {emailConfig.config.from}
                      </p>
                    </div>
                  </div>
                )}

                {/* Botão Recarregar */}
                <button
                  onClick={checkEmailConfig}
                  disabled={loading}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Recarregar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Teste de Email */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Send className="h-5 w-5 mr-2 text-gray-500" />
              Enviar Email de Teste
            </h2>
          </div>
          <div className="px-6 py-4">
            <form onSubmit={sendTestEmail} className="space-y-4">
              <div>
                <label htmlFor="test-email" className="block text-sm font-medium text-gray-700">
                  Email de Destino
                </label>
                <input
                  type="email"
                  id="test-email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Digite o email para teste"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              {message && (
                <div className={`rounded-md p-4 ${message.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex">
                    {message.type === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-400 mr-2" />
                    )}
                    <span className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                      {message.text}
                    </span>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={sending || !emailConfig?.configured}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Email de Teste
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Instruções de Configuração */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-gray-500" />
              Instruções de Configuração
            </h2>
          </div>
          <div className="px-6 py-4">
            {/* Aviso Digital Ocean */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">
                    Digital Ocean bloqueia portas SMTP padrão
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    As portas 25, 465 e 587 são bloqueadas. Use a porta 2525 ou APIs REST.
                  </p>
                </div>
              </div>
            </div>

            {/* Provedores Recomendados */}
            <div className="space-y-6">
              <h3 className="text-md font-medium text-gray-900">Provedores Recomendados</h3>
              
              {/* SendGrid */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">SendGrid (Recomendado)</h4>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    100 emails/dia grátis
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Use a porta 2525 que funciona na Digital Ocean.
                </p>
                <pre className="mt-3 bg-gray-800 text-gray-100 rounded-md p-3 text-xs overflow-x-auto">
{`EMAIL_PROVIDER=smtp
EMAIL_HOST=smtp.sendgrid.net
EMAIL_PORT=2525
EMAIL_USER=apikey
EMAIL_PASS=SG.sua_api_key_aqui`}
                </pre>
              </div>

              {/* Resend */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Resend (API REST)</h4>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    3000 emails/mês grátis
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Não usa portas SMTP - perfeito para Digital Ocean.
                </p>
                <pre className="mt-3 bg-gray-800 text-gray-100 rounded-md p-3 text-xs overflow-x-auto">
{`EMAIL_PROVIDER=resend
EMAIL_PASS=re_sua_api_key_aqui
EMAIL_FROM=noreply@seudominio.com`}
                </pre>
              </div>

              {/* Brevo */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Brevo (Sendinblue)</h4>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    300 emails/dia grátis
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Suporta porta 2525 alternativa.
                </p>
                <pre className="mt-3 bg-gray-800 text-gray-100 rounded-md p-3 text-xs overflow-x-auto">
{`EMAIL_PROVIDER=smtp
EMAIL_HOST=smtp-relay.brevo.com
EMAIL_PORT=2525
EMAIL_USER=seu_email@exemplo.com
EMAIL_PASS=sua_chave_smtp`}
                </pre>
              </div>
            </div>

            {/* Info Adicional */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6">
              <div className="flex">
                <Info className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-800">
                    <strong>Variáveis de ambiente:</strong> Configure as variáveis no arquivo <code className="bg-blue-100 px-1 rounded">.env</code> do backend 
                    ou via Docker Secrets em produção.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfigPage;
